import './PushForm.scss';

import React, { useState, useEffect, useCallback } from 'react';
import usePushTypes from 'hooks/usePushTypes';
import useForm from 'hooks/useLocalizedSimpleForm';
import { PushValidators } from 'validators/ExperienceCanvas/push.validator';
import { mapPushTypes } from 'helpers/push.helper';
import { getDefaultLanguage } from 'helpers/templated-experience.helper';
import useSettings from 'hooks/useSetting';
import { PushPayloadMetadata } from 'components/ExperienceCanvas/types';
import CompleteIcon from 'pages/ExperienceCanvas/assets/icons/CompleteIcon';
import InAppContentIcon from 'pages/ExperienceCanvas/assets/icons/InAppContentIcon';
import LocalizationSelector from 'components/ExperienceCanvas/LocalizationSelector/LocalizationSelector';
import ContentEditableAreaForContextAttributes from 'components/ExperienceCanvas/TouchpointEditor/TouchpointTimelineBox/PushEditorBox/ContentEditableAreaForContextAttributes/ContentEditableAreaForContextAttributes';
import WeblinkForm from './WeblinkForm';
import ActionLinkForm from './ActionLinkForm';
import PushFormAdvanced from './PushFormAdvanced';
import { PushFormProps } from '../pushDraft.types';

function BasicFormMessage() {
  return (
    <div className={`message-box success`}>
      <CompleteIcon />
      <span> Push will take user to home-screen or custom configured </span>
    </div>
  );
}

function InAppContentMessage() {
  return (
    <div className={`message-box info`}>
      <InAppContentIcon />
      <span> The push will lead the user to the primary in-app content </span>
    </div>
  );
}

const DEFAULT_CLASS = 'push-editor';

export default function PushForm({
  pushDraft,
  ruleDraft,
  stepIdx,
  isJourneyLocked,
  pushPayloadTypeConstrained,
  isReadonly = false,
  onChangePushField,
  onSelectPayloadType,
  onChangeRuleField,
}: PushFormProps) {
  const { languages, isInitialized: isLangInit } = useSettings();
  const defaultLang = getDefaultLanguage(languages);
  const { pushTypes, isInitialized: isPushTypesInit } = usePushTypes();

  const [pushPayloadTabs, setPushPayloadTabs] = useState<PushPayloadMetadata[]>([]);
  const pushPayloadType = pushDraft.pushPayloadType ?? 'basic';

  const selectPayloadType = useCallback(
    (pushType: PushPayloadMetadata) => {
      onSelectPayloadType(pushType.key, pushType.id || '');
    },
    [onSelectPayloadType],
  );

  useEffect(() => {
    if (isPushTypesInit && !pushPayloadTabs.length) {
      const newPayloadTabs = mapPushTypes(pushTypes);
      setPushPayloadTabs(newPayloadTabs);
      const newPayloadType = newPayloadTabs.find((type) => type.key === pushPayloadType);
      if (newPayloadType && !pushDraft.pushPayloadTypeId) {
        selectPayloadType(newPayloadType);
      }
      selectLanguageTab(defaultLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushTypes, isPushTypesInit, pushPayloadTabs, pushPayloadType, selectPayloadType]);

  const [selectedLang, setLanguage] = useState(isLangInit ? defaultLang : '');
  const [selectedLangDirection, setLanguageDirection] = useState(
    languages.find(({ id }) => id === defaultLang)?.direction || 'auto',
  );
  useEffect(() => {
    if (!selectedLang && isLangInit) {
      setLanguage(defaultLang);
    }
  }, [isLangInit, selectedLang, languages, defaultLang]);

  const {
    formData: pushBody,
    updateField,
    toggleError,
  } = useForm({
    data: {
      title: {
        value: pushDraft.title?.[selectedLang] ?? '',
      },
      alert: {
        value: pushDraft.alert?.[selectedLang] ?? '',
      },
    },
    validators: PushValidators,
    lang: selectedLang,
    defaultLang: defaultLang,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const selectLanguageTab = (lang: string) => {
    setLanguage(lang);
    const language = languages.find(({ id }) => id === lang);
    setLanguageDirection(language?.direction || 'auto');
  };

  const handleBlur = (key: 'title' | 'alert') => {
    toggleError(key);
    onChangePushField(key, selectedLang, pushBody[key].value);
  };

  const handleRichEditorTextChange = (name: string, value: string) => {
    updateField(name, value);
    onChangePushField(name as 'title' | 'alert', selectedLang, value);
  };

  const RenderPushPayloadTabs = () => {
    return pushPayloadTabs.map((payloadTab) => {
      const activeClass = payloadTab.key === pushPayloadType ? 'active' : '';
      return (
        <button
          key={`pep-${payloadTab.key}`}
          className={`${DEFAULT_CLASS}__payload-type-tab ${activeClass}`}
          aria-label={'select push payload type'}
          disabled={isReadonly || pushPayloadTypeConstrained}
          onClick={() => {
            selectPayloadType(payloadTab);
          }}
        >
          <payloadTab.icon fill="" className={`${DEFAULT_CLASS}__payload-type-tab-icon`} />
          <span>{payloadTab.title}</span>
        </button>
      );
    });
  };

  const RenderPushPayloadForm = () => {
    switch (pushPayloadType) {
      case 'basic':
        return <BasicFormMessage />;
      case 'content':
        return <InAppContentMessage />;
      case 'weblink':
        return (
          <WeblinkForm
            pushDraft={pushDraft}
            activeLang={selectedLang}
            defaultLang={defaultLang}
            onChangePushField={onChangePushField}
          />
        );
      case 'actionlink':
        return (
          <ActionLinkForm
            pushDraft={pushDraft}
            activeLang={selectedLang}
            defaultLang={defaultLang}
            onChangePushField={onChangePushField}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (pushDraft.pushPayloadType) {
      toggleError('title');
      toggleError('alert');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isPushTypesInit && isLangInit && (
        <div className={`${DEFAULT_CLASS} ${isReadonly ? `${DEFAULT_CLASS}--readonly` : ''}`} inert={isReadonly}>
          {languages.length > 1 && (
            <LocalizationSelector
              label="Push language"
              tooltip="Changing languages lets you write push notifications in the selected language"
              selectedLanguage={selectedLang}
              onChange={(lang) => selectLanguageTab(lang)}
            />
          )}
          <div className={`${DEFAULT_CLASS}__push-body`} dir={selectedLangDirection}>
            <ContentEditableAreaForContextAttributes
              className={`${DEFAULT_CLASS}__push-body-ti`}
              width="100%"
              label="Title"
              maxLength={170}
              hasError={pushBody.title.showError}
              errorMessage={pushBody.title.error}
              value={pushBody.title.value}
              onChange={(value: string) => handleRichEditorTextChange('title', value)}
              onBlur={() => {
                handleBlur('title');
              }}
              hideCharCounter
            />
            <span className={`${DEFAULT_CLASS}__push-body-subtext`} dir={selectedLangDirection}>
              Suggested maximum length: 1 line
            </span>
            <ContentEditableAreaForContextAttributes
              className={`${DEFAULT_CLASS}__push-body-ta`}
              width="100%"
              label="Message"
              maxLength={250}
              hasError={pushBody.alert.showError}
              errorMessage={pushBody.alert.error}
              value={pushBody.alert.value}
              onChange={(value: string) => handleRichEditorTextChange('alert', value)}
              onBlur={() => {
                handleBlur('alert');
              }}
            />
            <span className={`${DEFAULT_CLASS}__push-body-subtext`} dir={selectedLangDirection}>
              Suggested maximum length: 3 lines
            </span>
          </div>
          <div className={`${DEFAULT_CLASS}__payload-type`}>{RenderPushPayloadTabs()}</div>
          <div className={`${DEFAULT_CLASS}__payload-form`}>{RenderPushPayloadForm()}</div>
          <PushFormAdvanced
            classPrefix={DEFAULT_CLASS}
            onToggle={() => {
              setShowAdvanced((prev) => !prev);
            }}
            stepIdx={stepIdx}
            isExpanded={isJourneyLocked || showAdvanced}
            pushDraft={pushDraft}
            ruleDraft={ruleDraft}
            isJourneyLocked={isJourneyLocked}
            onChangePushField={onChangePushField}
            onChangeRuleField={onChangeRuleField}
          />
        </div>
      )}
    </>
  );
}
